/**
 * Task 24: OAuth Callback Handler
 * Receives JWT token from OAuth flow and stores it
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('Completing sign-in...');

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Get token and provider from URL params
      const token = searchParams.get('token');
      const provider = searchParams.get('provider');

      if (!token) {
        setStatus('error');
        setMessage('No authentication token received. Redirecting to login...');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Decode JWT to get user info (basic decoding without verification - backend already verified)
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      const user = {
        id: payload.sub,
        email: payload.email,
        role: payload.role,
        name: payload.email.split('@')[0], // Fallback name
      };

      // Store token and user info
      localStorage.setItem('spes_token', token);
      localStorage.setItem('spes_role', user.role);
      localStorage.setItem('spes_user', JSON.stringify(user));

      // Show success message
      setStatus('success');
      setMessage(`Successfully signed in with ${provider || 'OAuth'}!`);

      console.log(`✅ OAuth login successful: ${user.email} (${user.role})`);

      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);

    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage('Failed to process authentication. Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-dark via-navy to-navy-light flex items-center justify-center px-4">
      <div className="bg-navy-dark/80 backdrop-blur-xl shadow-2xl rounded-2xl p-8 border border-teal/20 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-teal animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-semibold text-gold mb-2">
              Processing...
            </h2>
            <p className="text-textLight/70 font-body">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-semibold text-gold mb-2">
              Success!
            </h2>
            <p className="text-textLight/70 font-body">{message}</p>
            <p className="text-sm text-textLight/50 font-body mt-4">
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-semibold text-red-400 mb-2">
              Error
            </h2>
            <p className="text-textLight/70 font-body">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
